import type { JSX } from 'react';
import { ShieldCheck, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/features/auth';
import type { RoleName } from '@/features/auth/types/auth.types';
import { formatRoleLabel } from '@/shared/constants/role-labels';

export function RoleSwitcher(): JSX.Element | null {
  const { activeRole, setActiveRole, getAvailableRoles } = useAuth();
  const availableRoles = getAvailableRoles();

  // Don't show if only one role or no company roles
  if (availableRoles.length <= 1) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid="role-switcher">
          <ShieldCheck className="mr-2 h-4 w-4" />
          {activeRole ? formatRoleLabel(activeRole) : 'Select Role'}
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Switch Role</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={activeRole ?? ''}
          onValueChange={(value) => setActiveRole(value as RoleName)}
        >
          {availableRoles.map((role) => (
            <DropdownMenuRadioItem key={role} value={role}>
              {formatRoleLabel(role)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
